import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, Loader2, ArrowLeft, User, Calendar, 
  CheckCircle, RefreshCw, ChevronDown, ChevronUp,
  Target, Clock, Filter
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminResolutionAuditPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [expandedId, setExpandedId] = useState(null);
  const [syncMessage, setSyncMessage] = useState(null);

  const isAdmin = user?.email === 'oanjorin@anjolc.com' || user?.is_admin;

  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !isAdmin)) {
      navigate('/');
    }
  }, [authLoading, isAuthenticated, isAdmin, navigate]);

  useEffect(() => {
    if (isAuthenticated && isAdmin) {
      fetchAuditLogs();
    }
  }, [isAuthenticated, isAdmin]);

  const getAuthHeaders = () => {
    const sessionToken = localStorage.getItem('session_token');
    return sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {};
  };

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      
      const res = await fetch(`${API_URL}/api/admin/resolution-audit`, {
        headers,
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        setAuditLogs(data.audit_logs || []);
        setTotalCount(data.total_count || 0);
      }
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncAudit = async () => {
    try {
      setSyncing(true);
      setSyncMessage(null);
      const headers = getAuthHeaders();
      
      const res = await fetch(`${API_URL}/api/admin/resolution-audit/sync`, {
        method: 'POST',
        headers,
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        setSyncMessage({
          type: 'success',
          text: `Sync complete! ${data.synced} new entries added, ${data.skipped} already existed. Total: ${data.total_audit_entries}`
        });
        fetchAuditLogs();
      } else {
        const error = await res.json();
        setSyncMessage({ type: 'error', text: error.detail || 'Failed to sync audit trail' });
      }
    } catch (error) {
      setSyncMessage({ type: 'error', text: 'Failed to sync audit trail' });
    } finally {
      setSyncing(false);
    }
  };

  const getOutcomeBadge = (outcome) => {
    const isYes = outcome === 'YES';
    return (
      <span className={`px-3 py-1 text-sm font-bold rounded-full ${
        isYes 
          ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
          : 'bg-red-500/20 text-red-400 border border-red-500/30'
      }`}>
        {outcome}
      </span>
    );
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#050810]">
        <Header />
        <div className="flex items-center justify-center py-40">
          <Loader2 className="h-8 w-8 text-[#3B82F6] animate-spin" />
          <span className="ml-3 text-gray-400">Loading resolution audit...</span>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050810]">
      <Header />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/admin')}
          className="text-gray-400 hover:text-white mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Admin Panel
        </Button>

        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-[#3B82F6]/20 rounded-xl">
              <FileText className="h-6 w-6 text-[#3B82F6]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Resolution Audit Trail</h1>
              <p className="text-gray-400 text-sm">Track all market resolutions with who, when, and why</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-gray-400 text-sm">
              Total: <span className="text-white font-semibold">{totalCount}</span> resolutions
            </span>
            <Button
              onClick={handleSyncAudit}
              disabled={syncing}
              variant="outline"
              className="border-green-500/50 text-green-400 hover:bg-green-500/10"
            >
              {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Sync All Resolved
            </Button>
            <Button
              onClick={fetchAuditLogs}
              variant="outline"
              className="border-[#1e2a45] text-gray-300"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Sync Message */}
        {syncMessage && (
          <div className={`mb-6 p-4 rounded-xl border flex items-center gap-3 ${
            syncMessage.type === 'success' 
              ? 'bg-green-500/10 border-green-500/30 text-green-400'
              : 'bg-red-500/10 border-red-500/30 text-red-400'
          }`}>
            {syncMessage.type === 'success' ? <CheckCircle className="h-5 w-5" /> : <Clock className="h-5 w-5" />}
            {syncMessage.text}
          </div>
        )}

        {/* Audit Logs List */}
        <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] overflow-hidden">
          {auditLogs.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="h-16 w-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No Resolution Records</h3>
              <p className="text-gray-400 mb-6">
                Market resolutions will appear here with full audit details.
                Click "Sync All Resolved" to import any existing resolved markets.
              </p>
              <Button
                onClick={handleSyncAudit}
                disabled={syncing}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                Sync All Resolved Markets
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-[#1e2a45]">
              {/* Table Header */}
              <div className="px-6 py-3 bg-[#1e2a45]/50 grid grid-cols-12 gap-4 text-sm font-medium text-gray-400">
                <div className="col-span-4">Market</div>
                <div className="col-span-2">Outcome</div>
                <div className="col-span-3">Resolved By</div>
                <div className="col-span-3">When</div>
              </div>

              {auditLogs.map((log) => (
                <div 
                  key={log.market_id}
                  className="hover:bg-[#1e2a45]/30 transition-colors"
                >
                  {/* Main Row */}
                  <div 
                    className="px-6 py-4 grid grid-cols-12 gap-4 items-center cursor-pointer"
                    onClick={() => setExpandedId(expandedId === log.market_id ? null : log.market_id)}
                  >
                    <div className="col-span-4">
                      <div className="flex items-center gap-2">
                        <Target className="h-4 w-4 text-[#3B82F6] flex-shrink-0" />
                        <span className="text-white font-medium truncate">
                          {log.market_title}
                        </span>
                      </div>
                      {log.market_category && (
                        <span className="text-xs text-gray-500 mt-1 block">
                          {log.market_category}
                        </span>
                      )}
                    </div>
                    <div className="col-span-2">
                      {getOutcomeBadge(log.winning_outcome)}
                    </div>
                    <div className="col-span-3">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-500" />
                        <div>
                          <span className="text-white text-sm block">
                            {log.resolved_by?.username || log.resolved_by?.email?.split('@')[0] || 'Admin'}
                          </span>
                          <span className="text-xs text-gray-500">
                            {log.resolved_by?.email}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="col-span-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-gray-500" />
                        <div>
                          <span className="text-white text-sm block">
                            {new Date(log.resolved_at).toLocaleDateString()}
                          </span>
                          <span className="text-xs text-gray-500">
                            {new Date(log.resolved_at).toLocaleTimeString()}
                          </span>
                        </div>
                      </div>
                      {expandedId === log.market_id ? (
                        <ChevronUp className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedId === log.market_id && (
                    <div className="px-6 pb-4">
                      <div className="bg-[#1e2a45]/50 rounded-xl p-4 space-y-4">
                        {/* Resolution Reason */}
                        <div>
                          <h4 className="text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                            <FileText className="h-4 w-4" />
                            Resolution Reason
                          </h4>
                          <p className="text-gray-400 bg-[#131b2e] rounded-lg p-3">
                            {log.resolution_reason || 'No reason provided'}
                          </p>
                        </div>

                        {/* Additional Details */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div>
                            <span className="text-xs text-gray-500 block mb-1">Market ID</span>
                            <span className="text-sm text-white font-mono">{log.market_id}</span>
                          </div>
                          <div>
                            <span className="text-xs text-gray-500 block mb-1">Category</span>
                            <span className="text-sm text-white">{log.market_category || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="text-xs text-gray-500 block mb-1">Market Created</span>
                            <span className="text-sm text-white">
                              {log.market_created_at 
                                ? new Date(log.market_created_at).toLocaleDateString() 
                                : 'N/A'}
                            </span>
                          </div>
                          <div>
                            <span className="text-xs text-gray-500 block mb-1">Resolved By User ID</span>
                            <span className="text-sm text-white font-mono">
                              {log.resolved_by?.user_id || 'N/A'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AdminResolutionAuditPage;
